
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { CloseOutlined, DeleteOutlined, EditOutlined, EllipsisOutlined } from '@ant-design/icons'
import _ from 'lodash'
import moment from 'moment/moment'
import { createPortal } from 'react-dom'
import { Button, Card, Dropdown } from 'shared/ui'
import styled from 'styled-components'

import { useAppSelector } from '../../../shared/config/store'

import { ANNOTATION_COMMENT_ACTION } from './AssetAnnotations'
import { StatusButton, StatusButtonType } from './components/StatusButton'
import { FormattedMessage } from './FormattedMessage'
import { MessageWriter } from './MessageWriter'
import { ANNOTATION_TYPE, normalizeAnnotationAbsCoordinates } from './utils/annotationUtils'

interface AnnotationCommentProps {
  assetRef: React.RefObject<HTMLDivElement>
  isVisible: boolean
  newAnnotation: any
  selectedAnnotation: any
  onClose: () => void
  onAction: (action: ANNOTATION_COMMENT_ACTION, data: any) => void
  onReplyToComment: (annotation: any) => void
  className?: string
  assetBoundingBox: { width: number, height: number }
  currentZoom: number,
  assetPosition: any
  boundingRect?: { left: number, top: number, right: number, bottom: number }
  stageMargin?: number
  containerOffset?: { x: number, y: number }
  assetContentSize?: {width: number, height: number, containerOffset?: { x: number, y: number }}
}

moment.updateLocale('en', {
  relativeTime: {
    future: (input) => (input === 'Just now' ? input : `in ${input}`),
    past: (input) => input === 'Just now' ? 'Just now' : `${input} ago`,
    s: 'Just now',
    ss: 'Just now',
    m: 'a minute',
    mm: '%d minutes',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years'
  }
})

const CARD_WIDTH = 312

const calculateCommentPosition = (
  annotation: any,
  scale = 1,
  assetBoundingBox?: { width: number, height: number },
  stageMargin = 0,
  containerOffset = { x: 0, y: 0 }
) => {
  let cardHeight = 70

  if (!annotation) return { topPos: 0, leftPos: 0, alignLeft: false }

  const { x, y, width, height } = normalizeAnnotationAbsCoordinates(annotation.geometry.absolute)

  const xShift = 42 // 1px + 32px + 9px - padding from rect + with of marker + padding from marker
  let yShift = 0

  if(annotation.type === ANNOTATION_TYPE.RECTANGLE) {
    cardHeight = 102
    yShift = -32
  }

  let topPos = y * scale + cardHeight + containerOffset.y
  let leftPos = x * scale + stageMargin / 2 + containerOffset.x
  const alignLeft = false

  switch (annotation.type) {
  case ANNOTATION_TYPE.RECTANGLE:
    topPos += height * scale + yShift
    leftPos += width * scale + xShift
    break
  case ANNOTATION_TYPE.POINT:
    topPos += yShift
    leftPos += xShift
    break
  default:
    topPos += (height * scale || 0)
    leftPos += (width * scale || 0)
  }
  return { topPos, leftPos, alignLeft }
}

export const AnnotationComment = ({
  assetContentSize,
  assetRef,
  isVisible,
  newAnnotation,
  selectedAnnotation,
  onClose,
  onAction,
  onReplyToComment,
  className,
  assetBoundingBox,
  currentZoom,
  assetPosition,
  boundingRect,
  stageMargin,
  containerOffset = { x: 0, y: 0 }
}: AnnotationCommentProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [commentText, setCommentText] = useState('')
  const authorName = newAnnotation?.data?.author_name || selectedAnnotation?.data?.author_name
  const currentAnnotation = newAnnotation || selectedAnnotation
  const createdAt = moment(currentAnnotation?.data?.created).fromNow()
  const [position, setPosition] = useState({ top: 0, left: 0, alignLeft: false })
  const [isPositionCalculated, setIsPositionCalculated] = useState(false)
  const inputRef = useRef<any>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const currentScale = currentZoom / 100
  const [isUserTheAuthor, setIsUserTheAuthor] = useState(false)
  const userData = useAppSelector((state) => state.user.user)
  const messageWriterRef = useRef<any>(null)

  // Дебаунсинг для обновления позиции
  const debouncedUpdatePosition = useMemo(
    () => _.debounce((updater) => {
      updater()
    }, 16), // ~60fps
    []
  )

  const handleDelete = useCallback((e) => {
    const annotation = newAnnotation || selectedAnnotation
    onAction(ANNOTATION_COMMENT_ACTION.DELETE, annotation)
    onClose()
  }, [newAnnotation, selectedAnnotation, onAction, onClose])

  const handleStatusChange = useCallback(() => {
    const annotation = newAnnotation || selectedAnnotation
    onAction(ANNOTATION_COMMENT_ACTION.RESOLVE, annotation)
  }, [newAnnotation, selectedAnnotation, onAction])

  const menuItems = [
    ...(selectedAnnotation?.data?.status !== 'resolved' ? [{
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Edit',
      onClick: () => setIsEditing(true),
    }] : []),
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete',
      onClick: handleDelete,
      danger: true,
    },
  ]

  const contextMenuItems = isUserTheAuthor ? menuItems : []

  useEffect(() => {
    const authorId = selectedAnnotation?.data?.author_id
    const userId = userData?.user_id
    if(!userId || !authorId) return
    setIsUserTheAuthor(authorId === userId)
  }, [selectedAnnotation, userData])

  useEffect(() => {
    if(newAnnotation) {
      setIsEditing(true)
      setCommentText('')
    }

    if (!newAnnotation) {
      setIsEditing(false)
      setCommentText('')
    }
  }, [newAnnotation])

  useEffect(() => {
    if (selectedAnnotation && selectedAnnotation?.data && selectedAnnotation?.data?.text) {
      setCommentText(selectedAnnotation?.data?.text)
    } else {
      setCommentText('')
    }

    const shouldBeEditing = !selectedAnnotation?.data?.text && selectedAnnotation?.data?.status !== 'resolved'
    setIsEditing(shouldBeEditing)
  }, [selectedAnnotation])

  useEffect(() => {
    if (!isVisible) {
      setIsPositionCalculated(false)
      return
    }

    const updatePosition = () => {
      const annotation = newAnnotation || selectedAnnotation
      if (!annotation || !assetRef.current) return

      const { topPos, leftPos, alignLeft } = calculateCommentPosition(annotation, currentScale, assetBoundingBox, stageMargin, containerOffset)
      const assetRect = assetRef.current.getBoundingClientRect()
      // offsetX is a left margin of video content
      let offsetX = 0
      if(assetContentSize) {
        offsetX = (Math.abs(assetRect.width - assetContentSize.width)) / 2 || 0
      }
      const scrollX = window.scrollX
      const scrollY = window.scrollY
      const top = topPos + assetRect.top + scrollY
      const left = leftPos + assetRect.left + offsetX + scrollX
      setPosition({
        top,
        left,
        alignLeft
      })
      setIsPositionCalculated(true)
    }

    // Используем дебаунсинг для плавного обновления позиции
    const updatePositionWithDebounce = () => {
      debouncedUpdatePosition(updatePosition)
    }

    updatePositionWithDebounce()
    window.addEventListener('scroll', updatePositionWithDebounce)
    window.addEventListener('resize', updatePositionWithDebounce)

    return () => {
      window.removeEventListener('scroll', updatePositionWithDebounce)
      window.removeEventListener('resize', updatePositionWithDebounce)
      debouncedUpdatePosition.cancel()
    }
  }, [isVisible, newAnnotation, selectedAnnotation, assetRef, assetBoundingBox, currentScale, boundingRect, assetContentSize, stageMargin, containerOffset])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      const input = inputRef.current?.resizableTextArea?.textArea
      if (input) {
        input.focus()
        const length = input.value.length
        input.setSelectionRange(length, length)
      }
    }
  }, [isEditing])



  const handleSubmit = (data: any) => {
    if (data.message.trim()) {
      if (isEditing && selectedAnnotation?.data?.text && selectedAnnotation?.data?.status === 'resolved') {
        return
      }
      
      if (isEditing && selectedAnnotation?.data?.text) {
        // Edit existing comment
        onAction(ANNOTATION_COMMENT_ACTION.EDIT, {
          ...selectedAnnotation,
          data: {
            ...selectedAnnotation?.data,
            ...data
          }
        })
      } else {
        // Add new comment
        onAction(ANNOTATION_COMMENT_ACTION.ADD, {
          ...newAnnotation,
          data: {
            ...newAnnotation?.data,
            ...data
          }
        })
      }
      setIsEditing(false)
    }
  }


  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    return true
  }

  const commentContent = (
    <SCommentCard
      className={className}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        transform: 'translateY(-100%)',
        ...(position.alignLeft ? { right: 'auto' } : {})
      }}
      onMouseDown={handleMouseDown}
    >
      {isEditing ? (
        <SMessageWriter
          ref={messageWriterRef}
          type={newAnnotation ? 'new' : 'edit'}
          className="comment-editor"
          placeholder="Add a comment..."
          defaultValue={commentText}
          onSend={handleSubmit}
          onClickOutside={onClose}
          initialFocus={isEditing}
        />
      ) : (
        <SCommentCardBody>
          <SCommentHeader>
            <SAuthorInfo>
              <SAuthorName>
                {authorName}
              </SAuthorName>

              <STimeAgo>
                {createdAt}
              </STimeAgo>
            </SAuthorInfo>

            <SActionButtons>
              {contextMenuItems.length > 0 && (
                <Dropdown
                  placement="bottomLeft"
                  trigger={['click']}
                  dropdownRender={() => (
                    <>
                      <CustomDropdownMenu>
                        {contextMenuItems.map(({ key, icon, label, onClick, danger }) => (
                          <MenuItem key={key}
                            onClick={() => {
                              onClick()
                              setIsDropdownOpen(false)
                            }}
                            danger={danger}
                          >
                            {icon}

                            {label}
                          </MenuItem>
                        ))}
                      </CustomDropdownMenu>

                    </>
                  )}
                  onOpenChange={(visible) => setIsDropdownOpen(visible)}
                >
                  <SMenuButton type="text"
                    icon={<EllipsisOutlined />}
                    $active={isDropdownOpen}
                  />
                </Dropdown>
              )}

              {!newAnnotation && (
                <StatusButton
                  commentType={StatusButtonType.ANNOTATION}
                  status={selectedAnnotation?.data?.status || 'open'}
                  onStatusChange={handleStatusChange}
                  showTooltip={true}
                />
              )}

              <SCloseButton
                onClick={onClose}
                icon={<CloseOutlined />}
              />
            </SActionButtons>
          </SCommentHeader>

          <SCommentContent>
            <FormattedMessage message={commentText} />
          </SCommentContent>

          <SReplyBtnRow>
            <SReplyBtn onClick={() => onReplyToComment(selectedAnnotation)}>
              Reply to
            </SReplyBtn>
          </SReplyBtnRow>
        </SCommentCardBody>
      )}
    </SCommentCard>
  )

  if (!isVisible || !isPositionCalculated) {
    return null
  }

  return createPortal(commentContent, document.body)
}

const SCommentCardBody = styled.div`
  padding: 12px;
`

const SMessageWriter = styled(MessageWriter)`
  &.comment-editor{
    margin-top: 0;
    
    div {
      border: 0;
    }

  }
`

const SCommentCard = styled(Card)`
  border-radius: 6px;
  width: ${CARD_WIDTH}px;
  max-width: 90%;
  background-color: #1F1F1F;
  z-index: 1000;
  & .ant-card-body {
    padding: 0;
  }
`
const SCommentHeader = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  align-items: center;
`

const SAuthorInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const SAuthorName = styled.span`
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  max-width: 500px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
`

const STimeAgo = styled.span`
  color: #888;
  font-size: 12px;
`

const SActionButtons = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  gap: 8px;

  button {
    width: 25px !important;
    height: 25px !important;
  }
`

const SMenuButton = styled(Button)<{ $active?: boolean }>`
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background-color: ${({ $active }) => ($active ? '#424242' : 'transparent')};
  border-radius: 4px;
  font-size: 16px !important;
`

const SCloseButton = styled(Button)`
  padding: 0;
  display: flex !important;
  background-color: transparent;
  border: none;
  color: #fff;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  box-shadow: none;

  &:hover {
    color: #fff !important;
    background-color: #ffffff19 !important;
  }

  &:active {
    color: #fff !important;
    background-color: #ffffff19 !important;
  }
`

const SCommentContent = styled.div`
  margin-bottom: 12px;
`

const CustomDropdownMenu = styled.div`
  background: #424242;
  box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 9px 28px 8px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 8px;
  min-width: 102px;
  display: flex;
  flex-direction: column;
`

const MenuItem = styled.div<{ danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  font-size: 14px;
  color: ${({ danger }) => (danger ? '#E86E6B' : 'rgba(255, 255, 255, 0.85)')};
  cursor: pointer;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
`

const SReplyBtnRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

const SReplyBtn = styled.button`
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  border: none;
  background-color: transparent;
  outline: none;
  padding: 0;
  box-shadow: none;
  cursor: pointer;
`
export default AnnotationComment
